import View                      from '@widesk-ui/components/View';
import React                     from 'react';
import { createPortal }          from 'react-dom';
import { observer }              from 'mobx-react-lite';
import currentEditorStore        from '@/TokamapEditor/stores/currentEditorStore';
import Kbd                       from '@/TokamapEditor/ux/Kbd';
import useDesignTokens           from '@/hooks/useDesignTokens';
import drawingShortcuts          from '@/TokamapEditor/helpers/drawingShortcuts';
import type { DrawingShapeKind } from '@/TokamapEditor/helpers/drawingShortcuts';
import type { DrawingShortcut }  from '@/TokamapEditor/helpers/drawingShortcuts';

const DrawingShortcuts = observer(() => {
	const editor = currentEditorStore.get;

	const [snapping, setSnapping] = React.useState(false);
	const [constrain, setConstrain] = React.useState(false);
	const [editing, setEditing] = React.useState(false);
	const [shapeKind, setShapeKind] = React.useState<DrawingShapeKind>();

	React.useEffect(() => {
		if (!editor) return;

		return editor.mapAdapter.on(event => {
			if (event.type === 'snapping:changed') setSnapping(event.enabled);
			if (event.type === 'constrain:changed') setConstrain(event.enabled);
			if (event.type === 'edit-mode:changed') setEditing(event.shapeId !== null);
			if (event.type === 'drawing:started') setShapeKind(event.shapeKind);
		});
	}, [editor]);

	const container = editor?.mapAdapter.getContainerElement();
	const shortcuts = drawingShortcuts({ drawing: currentEditorStore.isDrawing, editing, shapeKind, snapping, constrain });

	if (!shortcuts.length || !container) return null;

	return createPortal(
		<View abs absB={16} absL={0} w="100%" flex center zIndex={10} style={{ pointerEvents: 'none', userSelect: 'none' }}>
			<DrawingShortcutsContent shortcuts={shortcuts} />
		</View>,
		container,
	);
});

export default DrawingShortcuts;

const DrawingShortcutsContent = ({ shortcuts }: { shortcuts: DrawingShortcut[]; }) => {
	const tokens = useDesignTokens();

	return (
		<View
			row
			centerV
			gap="md"
			px={14}
			py={6}
			br={999}
			bg={tokens.surface.elevated}
			size={12}
			style={{ boxShadow: tokens.shadow.elevated }}
		>
			{shortcuts.map(shortcut => <Shortcut key={shortcut.keyLabel} {...shortcut} />)}
		</View>
	);
};

const Shortcut = ({ keyLabel, label, active }: { keyLabel: string; label: string; active?: boolean; }) => {
	const tokens = useDesignTokens();

	return (
		<View row centerV gap="xs" color={active ? tokens.color.primary : tokens.text.secondary}>
			<Kbd active={active}>{keyLabel}</Kbd>

			<span>{label}</span>
		</View>
	);
};
